"use client";

import { useActionState, useEffect } from "react";

import { joinWaitlist, type WaitlistState } from "@/marketing/waitlist-action";
import { track } from "@/marketing/analytics";

const initialState: WaitlistState = { status: "idle" };

/**
 * Formulario de waitlist: un solo campo (mínima fricción) + honeypot.
 * `location` distingue instancias (hero / cta-final) en analytics.
 */
export function WaitlistForm({
  location,
  compact = false,
}: {
  location: string;
  compact?: boolean;
}) {
  const [state, formAction, pending] = useActionState(joinWaitlist, initialState);

  useEffect(() => {
    if (state.status === "success") track("waitlist_success", { location });
    if (state.status === "error") track("waitlist_error", { location });
  }, [state.status, location]);

  if (state.status === "success") {
    return (
      <div
        role="status"
        className="flex items-center gap-3 rounded-xl border border-[var(--accent-200)] bg-[var(--accent-50)] px-5 py-4 text-[15px] text-[var(--accent-950)]"
      >
        <span aria-hidden className="text-lg">✓</span>
        <p className="m-0">
          <strong className="font-semibold">Listo, estás en la lista.</strong>{" "}
          Te escribimos cuando llegue tu invitación — revisá tu correo.
        </p>
      </div>
    );
  }

  return (
    <form
      action={formAction}
      onSubmit={() => track("waitlist_submitted", { location })}
      className={`flex w-full flex-col gap-3 sm:flex-row ${compact ? "" : "max-w-xl"}`}
    >
      <label htmlFor={`waitlist-email-${location}`} className="sr-only">
        Tu email
      </label>
      <input
        id={`waitlist-email-${location}`}
        type="email"
        name="email"
        required
        autoComplete="email"
        placeholder="tu@email.com"
        className="h-12 flex-1 rounded-xl border border-[var(--ink-200)] bg-white px-4 text-[15px] text-[var(--ink-900)] outline-none transition-shadow placeholder:text-[var(--ink-400)] focus:border-[var(--accent-400)] focus:ring-2 focus:ring-[var(--accent-100)]"
      />
      {/* Honeypot: invisible para humanos */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      <button
        type="submit"
        disabled={pending}
        className="h-12 shrink-0 rounded-xl bg-[var(--accent-700)] px-6 text-[15px] font-semibold text-white transition-colors hover:bg-[var(--accent-800)] disabled:opacity-60"
      >
        {pending ? "Anotando…" : "Sumarme a la lista"}
      </button>
      {state.status === "error" && (
        <p className="m-0 w-full text-sm text-[var(--danger)] sm:order-last">
          {state.message}
        </p>
      )}
    </form>
  );
}
